class MondoCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseFloat(this.dataset.freeShippingThreshold) || 75;
    this.emptyCollection = this.dataset.emptyCollection || 'all';
    this.shippingAmountAnimationFrame = null;
    this.previousShippingRemainingCents = Math.round(this.threshold * 100);
    this.shippingMessageInitialized = false;
    this.activeElement = null;
    this.headerCartIcon = null;
    this._onCartIconClick = null;
    this._onCartIconKeydown = null;
    this._pubSubUnsubscriber = null;
    this._isInitialized = false;
    this._onDocumentKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('active')) this.close();
    };
  }

  _bindEvents() {
    this.panel = this.querySelector('.mondo-cart-drawer__panel');

    this.addEventListener('click', (event) => {
      if (!this.classList.contains('active') || !this.panel) return;
      if (event.target.closest('.mondo-cart-drawer__close')) {
        this.close();
        return;
      }
      if (!this.panel.contains(event.target)) {
        this.close();
      }
    });

    document.addEventListener('keydown', this._onDocumentKeydown);
  }

  connectedCallback() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    this._bindEvents();
    // Remove Dawn's CartNotification before it can show
    document.querySelector('cart-notification')?.remove();

    this.setHeaderCartIconAccessibility();

    // Subscribe to Dawn's pubsub for cart updates (add-to-cart events)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._pubSubUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    } else {
      // Fallback: wait for pubsub to be available
      window.addEventListener('load', () => {
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._pubSubUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        }
      });
    }
  }

  disconnectedCallback() {
    if (this._pubSubUnsubscriber) {
      this._pubSubUnsubscriber();
      this._pubSubUnsubscriber = null;
    }

    if (this.headerCartIcon && this._onCartIconClick && this._onCartIconKeydown) {
      this.headerCartIcon.removeEventListener('click', this._onCartIconClick);
      this.headerCartIcon.removeEventListener('keydown', this._onCartIconKeydown);
    }

    this.headerCartIcon = null;
    this._onCartIconClick = null;
    this._onCartIconKeydown = null;
    document.removeEventListener('keydown', this._onDocumentKeydown);
    this._isInitialized = false;
  }

  onCartUpdate(event) {
    if (event?.source === 'product-form' || event?.source === 'cart-items') return;
    this.open();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink || cartLink === this.headerCartIcon) return;

    if (this.headerCartIcon && this._onCartIconClick && this._onCartIconKeydown) {
      this.headerCartIcon.removeEventListener('click', this._onCartIconClick);
      this.headerCartIcon.removeEventListener('keydown', this._onCartIconKeydown);
    }

    this.headerCartIcon = cartLink;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    this._onCartIconClick = (event) => {
      event.preventDefault();
      this.open(cartLink);
    };

    this._onCartIconKeydown = (event) => {
      const keyCode = event.code ? event.code.toUpperCase() : '';
      if (['SPACE', 'ENTER'].includes(keyCode)) {
        event.preventDefault();
        this.open(cartLink);
      }
    };

    cartLink.addEventListener('click', this._onCartIconClick);
    cartLink.addEventListener('keydown', this._onCartIconKeydown);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  renderContents(parsedState) {
    this._updateCartBubble(parsedState);
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.open(this.activeElement);
  }

  async open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.setAttribute('aria-hidden', 'false');
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');

    const panel = this.querySelector('.mondo-cart-drawer__panel');
    if (panel) panel.focus();

    if (typeof trapFocus !== 'undefined') {
      trapFocus(panel);
    }

    // Only show loading spinner on first open (items container is empty)
    const itemsEl = this.querySelector('#MondoCartItems');
    const isEmpty = !itemsEl || itemsEl.children.length === 0;
    if (isEmpty) {
      this._renderLoading();
    }

    try {
      const cart = await this.fetchCart();
      this._syncCartBubbleCount(cart);
      this.renderItems(cart);
      this.updateShippingBar(cart);
      this.updateSubtotal(cart);
      if (cart.items && cart.items.length > 0) {
        this.loadRecommendations(cart);
      }
    } catch (err) {
      console.error('[MondoCartDrawer] Failed to load cart:', err);
    }
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus !== 'undefined') {
      removeTrapFocus(this.activeElement);
    }
  }

  async fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Cart fetch failed');
    return res.json();
  }

  _renderLoading() {
    const itemsEl = this.querySelector('#MondoCartItems');
    if (itemsEl) itemsEl.innerHTML = '<div class="mondo-cart-drawer__loading"></div>';
    this.querySelector('#MondoCartEmpty')?.classList.remove('visible');
    this.querySelector('#MondoCartFooter')?.classList.remove('visible');
    this.querySelector('#MondoCartRecommendations')?.classList.remove('visible');
  }

  renderItems(cart) {
    const itemsEl = this.querySelector('#MondoCartItems');
    const emptyEl = this.querySelector('#MondoCartEmpty');
    const footerEl = this.querySelector('#MondoCartFooter');

    if (!itemsEl) return;

    if (!cart.items || cart.items.length === 0) {
      itemsEl.innerHTML = '';
      emptyEl?.classList.add('visible');
      footerEl?.classList.remove('visible');
      this.loadEmptyStateProducts();
      return;
    }

    emptyEl?.classList.remove('visible');
    footerEl?.classList.add('visible');

    itemsEl.innerHTML = cart.items.map((item, index) => {
      const line = index + 1;
      const isSale = item.original_line_price !== item.final_line_price;
      const priceHtml = isSale
        ? `<s class="mondo-cart-item__price--original">${this._formatMoney(item.original_line_price)}</s><span class="mondo-cart-item__price--sale">${this._formatMoney(item.final_line_price)}</span>`
        : this._formatMoney(item.final_line_price);
      const imgSrc = this._getSizedImageUrl(
        item.featured_image?.url || item.image || item.featured_media?.preview_image?.src,
        '120x120'
      );
      const imgHtml = imgSrc
        ? `<img src="${imgSrc}" alt="${this._escapeHtml(item.title)}" width="80" height="80" class="mondo-cart-item__image" loading="lazy">`
        : '';

      return `
        <div class="mondo-cart-item" data-line="${line}">
          <a href="${item.url}" class="mondo-cart-item__image-link">
            ${imgHtml}
          </a>
          <div class="mondo-cart-item__details">
            <div class="mondo-cart-item__title">${this._escapeHtml(item.product_title)}</div>
            ${item.variant_title ? `<div class="mondo-cart-item__variant">${this._escapeHtml(item.variant_title)}</div>` : ''}
            <div class="mondo-cart-item__price">${priceHtml}</div>
            <div class="mondo-cart-item__actions">
              <div class="mondo-qty-stepper">
                <button class="mondo-qty-stepper__btn" type="button" aria-label="Decrease quantity" data-action="decrease" data-line="${line}">−</button>
                <span class="mondo-qty-stepper__count">${item.quantity}</span>
                <button class="mondo-qty-stepper__btn" type="button" aria-label="Increase quantity" data-action="increase" data-line="${line}">+</button>
              </div>
              <button class="mondo-cart-item__remove" type="button" aria-label="Remove ${this._escapeHtml(item.product_title)}" data-line="${line}" data-action="remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                  <path d="M10 11v6M14 11v6"></path>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // Attach quantity/remove listeners
    this._attachItemListeners(itemsEl);
  }

  _attachItemListeners(itemsEl) {
    itemsEl.querySelectorAll('[data-action]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const line = parseInt(btn.dataset.line, 10);
        const action = btn.dataset.action;

        if (action === 'remove') {
          await this.updateQuantity(line, 0);
        } else {
          const countEl = btn.closest('.mondo-qty-stepper')?.querySelector('.mondo-qty-stepper__count');
          const currentQty = parseInt(countEl?.textContent || '1', 10);
          const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
          await this.updateQuantity(line, newQty);
        }
      });
    });
  }

  async updateQuantity(line, quantity) {
    const itemsEl = this.querySelector('#MondoCartItems');
    const itemEl = this.querySelector(`.mondo-cart-item[data-line="${line}"]`);

    // Disable all action buttons immediately
    this._setUpdating(true);
    this._setItemsDisabled(true);

    if (quantity === 0 && itemEl) {
      // Fade out before removing
      itemEl.classList.add('removing');
    } else if (itemEl) {
      // Optimistic qty update
      itemEl.querySelector('.mondo-qty-stepper__count').textContent = quantity;
    }

    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ['cart-icon-bubble'] }),
      });
      if (!res.ok) throw new Error('Cart update failed');
      const response = await res.json();

      // Fetch fresh cart for accurate prices
      const cart = await this.fetchCart();
      this._syncCartBubbleCount(cart);

      if (quantity === 0) {
        // Wait for fade transition, then remove
        setTimeout(() => {
          itemEl?.remove();
          this._renumberLines();
        }, 200);
      } else {
        // Update line price in-place
        const newItem = cart.items[line - 1];
        if (newItem && itemEl) {
          const priceEl = itemEl.querySelector('.mondo-cart-item__price');
          const isSale = newItem.original_line_price !== newItem.final_line_price;
          priceEl.innerHTML = isSale
            ? `<s class="mondo-cart-item__price--original">${this._formatMoney(newItem.original_line_price)}</s><span class="mondo-cart-item__price--sale">${this._formatMoney(newItem.final_line_price)}</span>`
            : this._formatMoney(newItem.final_line_price);
        }
      }

      // If cart is now empty, show empty state
      if (!cart.items || cart.items.length === 0) {
        // Wait for removal fade before showing empty state
        setTimeout(() => {
          if (itemsEl) itemsEl.innerHTML = '';
          this.querySelector('#MondoCartEmpty')?.classList.add('visible');
          this.querySelector('#MondoCartFooter')?.classList.remove('visible');
          this.querySelector('#MondoCartRecommendations')?.classList.remove('visible');
          this.loadEmptyStateProducts();
        }, 200);
      }

      // Update shipping state for both non-empty and empty carts.
      this.updateShippingBar(cart);
      this.updateSubtotal(cart);

      // Update Dawn's cart icon bubble
      this._updateCartBubble(response);

      // Notify pubsub
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: response });
      }
    } catch (err) {
      console.error('[MondoCartDrawer] updateQuantity failed:', err);
      // Revert optimistic update on error — do full re-render
      const cart = await this.fetchCart();
      this._syncCartBubbleCount(cart);
      this.renderItems(cart);
      this.updateShippingBar(cart);
      this.updateSubtotal(cart);
    } finally {
      this._setItemsDisabled(false);
      this._setUpdating(false);
    }
  }

  _setUpdating(isUpdating) {
    this.classList.toggle('is-updating', isUpdating);
    this.setAttribute('aria-busy', isUpdating ? 'true' : 'false');
  }

  _setItemsDisabled(disabled) {
    const itemsEl = this.querySelector('#MondoCartItems');
    if (!itemsEl) return;
    if (disabled) {
      itemsEl.classList.add('updating');
    } else {
      itemsEl.classList.remove('updating');
    }
  }

  _renumberLines() {
    const items = this.querySelectorAll('.mondo-cart-item');
    items.forEach((item, index) => {
      const newLine = index + 1;
      item.dataset.line = newLine;
      item.querySelectorAll('[data-line]').forEach((el) => {
        el.dataset.line = newLine;
      });
    });
  }

  _updateCartBubble(response) {
    if (response.sections && response.sections['cart-icon-bubble']) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(response.sections['cart-icon-bubble'], 'text/html');
      const newBubble = doc.querySelector('#cart-icon-bubble');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
        this.setHeaderCartIconAccessibility();
      }
    }
  }

  _syncCartBubbleCount(cart) {
    const currentBubble = document.getElementById('cart-icon-bubble');
    if (!currentBubble) return;

    const itemCount = Number(cart?.item_count) || 0;
    let countBubble = currentBubble.querySelector('.cart-count-bubble');

    if (itemCount <= 0) {
      countBubble?.remove();
      return;
    }

    if (!countBubble) {
      countBubble = document.createElement('div');
      countBubble.className = 'cart-count-bubble';
      countBubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      currentBubble.appendChild(countBubble);
    }

    const visibleCount = countBubble.querySelector('[aria-hidden="true"]');
    const srCount = countBubble.querySelector('.visually-hidden');

    if (visibleCount) {
      visibleCount.textContent = itemCount < 100 ? String(itemCount) : '';
    }

    if (srCount) {
      srCount.textContent = `${itemCount} item${itemCount === 1 ? '' : 's'} in cart`;
    }
  }

  updateShippingBar(cart) {
    const thresholdCents = Math.round(this.threshold * 100);
    const subtotalCents = Number(cart.total_price) || 0;

    // Guard: threshold of 0 or unset means feature is disabled
    if (!thresholdCents) return;

    const unlocked = subtotalCents >= thresholdCents;
    const remainingCents = Math.max(thresholdCents - subtotalCents, 0);

    const barEl = this.querySelector('.mondo-cart-drawer__shipping-bar');
    const textEl = this.querySelector('#MondoShippingBarText');
    const prefixEl = textEl?.querySelector('[data-shipping-prefix]');
    const amountEl = textEl?.querySelector('[data-shipping-amount]');
    const suffixEl = textEl?.querySelector('[data-shipping-suffix]');

    if (barEl) {
      barEl.classList.toggle('is-unlocked', unlocked);
    }

    const progressFill = this.querySelector('#MondoProgressFill');
    if (progressFill) {
      const pct = Math.min((subtotalCents / thresholdCents) * 100, 100);
      progressFill.style.width = `${pct}%`;
    }

    if (textEl) {
      this._animateShippingMessage({
        textEl,
        prefixEl,
        amountEl,
        suffixEl,
        unlocked,
        remainingCents,
        previousRemainingCents: this.shippingMessageInitialized
          ? this.previousShippingRemainingCents
          : remainingCents,
      });
    }

    this.previousShippingRemainingCents = remainingCents;
    this.shippingMessageInitialized = true;
  }

  updateSubtotal(cart) {
    const subtotalEl = this.querySelector('#MondoCartSubtotal');
    if (subtotalEl) subtotalEl.textContent = this._formatMoney(cart.total_price);
  }

  async loadEmptyStateProducts() {
    const containerEl = this.querySelector('#MondoEmptyProducts');
    if (!containerEl) return;

    try {
      const res = await fetch(`/collections/${this.emptyCollection}/products.json?limit=6`);
      if (!res.ok) return;
      const data = await res.json();
      const products = data.products || [];
      if (products.length === 0) return;

      containerEl.innerHTML = products.map((product) => this._renderRecommendationCard(product)).join('');

      containerEl.querySelectorAll('.mondo-rec-card__add').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const variantId = parseInt(btn.dataset.variantId, 10);
          btn.disabled = true;
          this._setUpdating(true);
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1, sections: ['cart-icon-bubble'] }),
            });
            if (!res.ok) throw new Error('Add to cart failed');
            const response = await res.json();
            this._updateCartBubble(response);
            const cart = await this.fetchCart();
            this._syncCartBubbleCount(cart);
            this.renderItems(cart);
            this.updateShippingBar(cart);
            this.updateSubtotal(cart);
            this.loadRecommendations(cart);
          } catch (err) {
            console.error('[MondoCartDrawer] add to cart failed:', err);
            btn.disabled = false;
          } finally {
            this._setUpdating(false);
          }
        });
      });
    } catch (err) {
      console.error('[MondoCartDrawer] loadEmptyStateProducts failed:', err);
    }
  }

  async loadRecommendations(cart) {
    const trackEl = this.querySelector('#MondoRecommendationsTrack');
    const recEl = this.querySelector('#MondoCartRecommendations');
    if (!trackEl || !recEl) return;

    const clearRecommendations = () => {
      trackEl.innerHTML = '';
      recEl.classList.remove('visible');
    };

    const productId = cart.items[0]?.product_id;
    if (!productId) {
      clearRecommendations();
      return;
    }

    const cartProductIds = new Set(cart.items.map((i) => i.product_id));

    try {
      const res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=2`);
      if (!res.ok) {
        clearRecommendations();
        return;
      }
      const data = await res.json();

      const filtered = (data.products || []).filter((p) => !cartProductIds.has(p.id)).slice(0, 2);
      if (filtered.length === 0) {
        clearRecommendations();
        return;
      }

      trackEl.innerHTML = filtered.map((product) => this._renderRecommendationCard(product)).join('');

      recEl.classList.add('visible');

      // Attach add listeners
      trackEl.querySelectorAll('.mondo-rec-card__add').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const variantId = parseInt(btn.dataset.variantId, 10);
          btn.disabled = true;
          this._setUpdating(true);
          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1, sections: ['cart-icon-bubble'] }),
            });
            if (!res.ok) throw new Error('Add to cart failed');
            const response = await res.json();
            this._updateCartBubble(response);
            const cart = await this.fetchCart();
            this._syncCartBubbleCount(cart);
            this.renderItems(cart);
            this.updateShippingBar(cart);
            this.updateSubtotal(cart);
            this.loadRecommendations(cart);
          } catch (err) {
            console.error('[MondoCartDrawer] add to cart failed:', err);
            btn.disabled = false;
          } finally {
            this._setUpdating(false);
          }
        });
      });
    } catch (err) {
      clearRecommendations();
      console.error('[MondoCartDrawer] loadRecommendations failed:', err);
    }
  }

  _formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  _animateShippingMessage({
    textEl,
    prefixEl,
    amountEl,
    suffixEl,
    unlocked,
    remainingCents,
    previousRemainingCents,
  }) {
    if (this.shippingAmountAnimationFrame) {
      cancelAnimationFrame(this.shippingAmountAnimationFrame);
      this.shippingAmountAnimationFrame = null;
    }

    if (!prefixEl || !amountEl || !suffixEl) return;

    textEl.classList.remove('is-changing');
    void textEl.offsetWidth;
    textEl.classList.add('is-changing');

    if (unlocked) {
      prefixEl.textContent = 'You unlocked free shipping';
      amountEl.textContent = '';
      amountEl.hidden = true;
      suffixEl.textContent = '';
      return;
    }

    prefixEl.textContent = 'Add';
    amountEl.hidden = false;
    amountEl.textContent = this._formatMoney(previousRemainingCents);
    suffixEl.textContent = 'to unlock free shipping';

    amountEl.classList.remove('is-updating');
    void amountEl.offsetWidth;
    amountEl.classList.add('is-updating');

    if (previousRemainingCents === remainingCents) {
      amountEl.textContent = this._formatMoney(remainingCents);
      return;
    }

    const duration = 320;
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(
        previousRemainingCents + (remainingCents - previousRemainingCents) * eased
      );
      amountEl.textContent = this._formatMoney(currentValue);

      if (progress < 1) {
        this.shippingAmountAnimationFrame = requestAnimationFrame(step);
      } else {
        this.shippingAmountAnimationFrame = null;
        amountEl.textContent = this._formatMoney(remainingCents);
      }
    };

    this.shippingAmountAnimationFrame = requestAnimationFrame(step);
  }

  _buildProductPriceHtml(product, variant) {
    const currentPrice = this._getPriceValue(
      variant?.price,
      product?.price,
      product?.price_min,
      product?.price_min_cents
    );
    const originalPrice = this._getPriceValue(
      variant?.compare_at_price,
      product?.compare_at_price,
      product?.compare_at_price_min,
      product?.compare_at_price_min_cents
    );
    const isSale = originalPrice && currentPrice && originalPrice > currentPrice;

    if (isSale) {
      return `<s class="mondo-rec-card__price--original price-item price-item--regular">${this._formatMoney(originalPrice)}</s><span class="mondo-rec-card__price--sale price-item price-item--sale">${this._formatMoney(currentPrice)}</span>`;
    }

    return `<span class="price-item price-item--regular">${this._formatMoney(currentPrice || 0)}</span>`;
  }

  _renderRecommendationCard(product) {
    const variant = product.variants?.[0];
    if (!variant) return '';

    const imgSrc = this._getSizedImageUrl(
      product.featured_image?.src || product.featured_image || product.images?.[0]?.src || product.image?.src,
      '533x533'
    );
    const priceHtml = this._buildProductPriceHtml(product, variant);
    const productUrl = this._getProductUrl(product);
    const isAvailable = product.available !== false && variant.available !== false;
    const title = this._escapeHtml(product.title);
    const badgeHtml = !isAvailable
      ? '<span class="badge badge--bottom-left mondo-rec-card__badge mondo-rec-card__badge--sold-out">Sold out</span>'
      : '';

    return `
      <div class="mondo-rec-card card-wrapper product-card-wrapper underline-links-hover">
        <div class="card card--standard card--media card--extend-height">
          <div class="card__inner color-background-1 gradient ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <a href="${productUrl}" class="mondo-rec-card__media-link" aria-label="${title}">
                <div class="media media--transparent">
                  ${imgSrc ? `<img src="${imgSrc}" alt="${title}" width="533" height="533" class="motion-reduce mondo-rec-card__image" loading="lazy">` : ''}
                </div>
              </a>
            </div>
            <div class="card__content">
              <div class="card__badge">${badgeHtml}</div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <h3 class="card__heading h5">
                <a href="${productUrl}" class="full-unstyled-link">${title}</a>
              </h3>
              <div class="card-information">
                <div class="mondo-rec-card__price price">
                  <div class="price__container">
                    ${priceHtml}
                  </div>
                </div>
              </div>
            </div>
            <div class="mondo-rec-card__actions featured-collection-mondo__card-actions">
              <button
                class="button mondo-rec-card__add"
                type="button"
                data-variant-id="${variant.id}"
                aria-label="Add ${title} to cart"
                ${isAvailable ? '' : 'disabled'}
              >${isAvailable ? 'Add to cart' : 'Sold out'}</button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  _getPriceValue(...values) {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;

      const normalized = this._normalizeProductMoneyValue(value);
      if (normalized !== null) return normalized;
    }

    return 0;
  }

  _normalizeProductMoneyValue(value) {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      if (trimmed.includes('.')) {
        const parsedFloat = Number.parseFloat(trimmed);
        return Number.isFinite(parsedFloat) ? Math.round(parsedFloat * 100) : null;
      }

      const parsedInt = Number(trimmed);
      return Number.isFinite(parsedInt) ? parsedInt : null;
    }

    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return null;
      return Number.isInteger(value) ? value : Math.round(value * 100);
    }

    return null;
  }

  _getProductUrl(product) {
    if (product?.url) return product.url;
    if (product?.handle) return `/products/${product.handle}`;
    return '#';
  }

  _productHasSale(product, variant) {
    const currentPrice = this._getPriceValue(
      variant?.price,
      product?.price,
      product?.price_min,
      product?.price_min_cents
    );
    const originalPrice = this._getPriceValue(
      variant?.compare_at_price,
      product?.compare_at_price,
      product?.compare_at_price_min,
      product?.compare_at_price_min_cents
    );

    return Boolean(originalPrice && currentPrice && originalPrice > currentPrice);
  }

  _getSizedImageUrl(image, size) {
    if (!image) return '';

    const rawUrl = typeof image === 'string' ? image : image.src || image.url || '';
    if (!rawUrl) return '';

    try {
      const url = new URL(rawUrl, window.location.origin);
      url.searchParams.delete('width');
      url.searchParams.delete('height');
      return `${url.origin}${url.pathname.replace(/(\.[^.]+)$/, `_${size}$1`)}`;
    } catch (err) {
      return rawUrl.replace(/(\.[^.]+)$/, `_${size}$1`);
    }
  }

  _escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', MondoCartDrawer);
}
